// src/admin/legal/AddVersionModal.jsx — «Nuova versione» (v219): document, version, the two links, the PDF and
// «In vigore dal» (Subito = effective_from omitted / Da una data = ISO with offset), sent as multipart.
// «Continua» validates (legalModel.validateNewVersion + sniffPdf), then a ConfirmDialog (stacked, plain «Conferma»)
// states the two consequences: frozen once in effect AND accepted; the website text must change at the same moment.
//
// ⛔ Errors raised inside the confirmation: a FIELD error (exists / past / links / pdf / 413) → onConfirm RESOLVES
// (the confirmation closes) and the error lands on its field; anything else → onConfirm THROWS our sentence, which
// ConfirmDialog shows in place. Never err.message.
// ⚠️ closeOnOverlay={false}: a half-filled form with a picked file must not vanish on a stray click (Esc / × close).
// ⚠️ Mounted conditionally by the page, so each opening starts clean.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AdminModal from '../components/AdminModal.jsx';
import ConfirmDialog from '../components/ConfirmDialog.jsx';
import { addLegalVersion } from '../legalDocumentsApi.js';
import {
  LEGAL_DOCUMENTS, validateNewVersion, sniffPdf, localToIso, toLocalInputValue, formatDateTime,
} from './legalModel.js';
import { legalErrorCopy } from './legalErrorCopy.js';
import { LinkFields, PdfField, FieldError } from './legalFields.jsx';
import { inputStyle, labelStyle, hintStyle, fieldStyle, buttonStyle } from './legalFormStyles.js';

const EMPTY = { document: 'tos', version: '', url: '', pdfUrl: '', file: null, when: 'now', effectiveLocal: '' };

export default function AddVersionModal({ onClose, onAdded }) {
  const { t, i18n } = useTranslation('admin');
  const [form, setForm] = useState(EMPTY);
  const [problems, setProblems] = useState({});
  const [checking, setChecking] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const problemText = (field) => {
    const p = problems[field];
    if (!p) return '';
    return p.startsWith('legalDocuments.') || p.startsWith('actionErrors.') ? t(p) : t(`legalDocuments.problems.${p}`);
  };

  async function handleContinue(e) {
    e.preventDefault();
    const found = validateNewVersion(form, Date.now());
    setProblems(found);
    if (Object.keys(found).length > 0) return;
    setChecking(true);
    const isPdf = await sniffPdf(form.file);
    setChecking(false);
    if (!isPdf) { setProblems({ file: 'pdfType' }); return; }
    setConfirming(true);
  }

  async function handleConfirm() {
    const effectiveFrom = form.when === 'date' ? localToIso(form.effectiveLocal) : null;
    try {
      const row = await addLegalVersion({
        document: form.document, version: form.version.trim(), url: form.url.trim(), pdfUrl: form.pdfUrl.trim(),
        file: form.file, effectiveFrom,
      });
      await onAdded(row, { scheduled: effectiveFrom !== null });
    } catch (err) {
      const copy = legalErrorCopy(err);
      if (copy.field) { setProblems({ [copy.field]: copy.key }); return; }
      throw new Error(t(copy.key, copy.params));
    }
  }

  const confirmMessage = form.when === 'date'
    ? t('legalDocuments.confirm.messageDate', {
      date: formatDateTime(localToIso(form.effectiveLocal), i18n.language),
    })
    : t('legalDocuments.confirm.messageNow');

  return (
    <AdminModal open onClose={onClose} title={t('legalDocuments.addTitle')} width={560} closeOnOverlay={false}>
      <form onSubmit={handleContinue} noValidate>
        <div style={fieldStyle}>
          <label htmlFor="lv-document" style={labelStyle}>{t('legalDocuments.form.document')}</label>
          <select id="lv-document" value={form.document} onChange={(e) => set('document')(e.target.value)} style={inputStyle}>
            {LEGAL_DOCUMENTS.map((d) => <option key={d} value={d}>{t(`legalDocuments.documents.${d}`)}</option>)}
          </select>
          <FieldError text={problemText('document')} />
        </div>
        <div style={fieldStyle}>
          <label htmlFor="lv-version" style={labelStyle}>{t('legalDocuments.form.version')}</label>
          <input id="lv-version" type="text" value={form.version} onChange={(e) => set('version')(e.target.value)}
            style={inputStyle} autoComplete="off" />
          <span style={hintStyle}>{t('legalDocuments.form.versionHint')}</span>
          <FieldError text={problemText('version')} />
        </div>
        <LinkFields
          url={form.url} pdfUrl={form.pdfUrl} onUrl={set('url')} onPdfUrl={set('pdfUrl')}
          problems={{ url: problemText('url'), pdfUrl: problemText('pdfUrl'), links: problemText('links') }}
        />
        <PdfField file={form.file} onFile={set('file')} hintKey="legalDocuments.form.fileHint" problem={problemText('file')} />
        <fieldset style={{ ...fieldStyle, border: 'none', padding: 0, margin: '0 0 16px' }}>
          <legend style={labelStyle}>{t('legalDocuments.form.effectiveFrom')}</legend>
          <label style={{ marginRight: '16px', fontSize: '14px' }}>
            <input type="radio" name="lv-when" checked={form.when === 'now'} onChange={() => set('when')('now')} />
            {' '}{t('legalDocuments.form.now')}
          </label>
          <label style={{ fontSize: '14px' }}>
            <input type="radio" name="lv-when" checked={form.when === 'date'} onChange={() => set('when')('date')} />
            {' '}{t('legalDocuments.form.fromDate')}
          </label>
          {form.when === 'date' && (
            <input
              type="datetime-local" aria-label={t('legalDocuments.form.effectiveFrom')}
              value={form.effectiveLocal} min={toLocalInputValue(new Date())}
              onChange={(e) => set('effectiveLocal')(e.target.value)}
              style={{ ...inputStyle, marginTop: '8px' }}
            />
          )}
          <FieldError text={problemText('effectiveFrom')} />
        </fieldset>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={buttonStyle(false)}>{t('common:actions.cancel')}</button>
          <button type="submit" disabled={checking} style={buttonStyle(true, checking)}>
            {checking ? t('legalDocuments.form.checking') : t('legalDocuments.form.continue')}
          </button>
        </div>
      </form>
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={handleConfirm}
        title={t('legalDocuments.confirm.title', { version: form.version.trim() })}
        message={confirmMessage}
      />
    </AdminModal>
  );
}
